/**
 * Block 5.1 (2026-05-26): Month-Close Runner API.
 */
import { api } from './client';
import type { Page } from './catalogs';


export type OperationType =
  | 'AMORTIZATION'
  | 'PAYROLL'
  | 'FX_REVALUATION'
  // Block 8.2 ФОП єдинник
  | 'SINGLE_TAX'
  | 'FOP_SELF_ESV'
  | 'FOP_SELF_WZ'
  // Block 11 ФОП ЗСО
  | 'FOP_ZSO_QUARTERLY'
  // Block 5.2 closing operations
  | 'EXPENSE_CLOSING'
  | 'REVENUE_CLOSING'
  | 'QUARTERLY_SNAPSHOT'
  | 'FINANCIAL_RESULT_CLOSING';

export type OperationStatus = 'SUCCESS' | 'FAILED' | 'SKIPPED';

export type MonthCloseStatus =
  | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED'
  | 'PARTIAL' | 'FAILED' | 'CANCELLED';


export interface OperationResult {
  operation_type: OperationType;
  status: OperationStatus;
  document_id: string | null;
  started_at: string;
  duration_ms: number;
  error_message: string | null;
  summary: Record<string, unknown>;
}

export interface PreCheckWarning {
  warning_type: string;
  count: number;
  items: Array<Record<string, unknown>>;
}

// Block 5.2 (2026-05-27): Closing snapshot computed з operations_results.
export interface ClosingSnapshot {
  expense_closing: {
    total_amount?: string;
    entries_count?: number;
    subconto_breakdown?: Array<{
      account_code: string;
      cost_item_id: string | null;
      project_id: string | null;
      amount: string;
    }>;
  } | null;
  revenue_closing: {
    total_amount?: string;
    entries_count?: number;
    accounts?: Array<{ account_code: string; amount: string }>;
  } | null;
  quarterly_snapshot: {
    quarter?: number;
    ytd_revenue?: string;
    ytd_expenses?: string;
    ytd_financial_result?: string;
  } | null;
  annual_closing: {
    financial_result?: string;
    transfer_account?: string | null;
    transfer_amount?: string;
    entries_count?: number;
  } | null;
}

export interface MonthCloseRun {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  period_year: number;
  period_month: number;
  status: MonthCloseStatus;
  started_at: string | null;
  completed_at: string | null;
  triggered_by_user_id: string | null;
  pre_check_passed: boolean;
  pre_check_warnings: PreCheckWarning[];
  operations_results: OperationResult[];
  closing_snapshot: ClosingSnapshot | null;
  created_at: string;
  updated_at: string;
}

export interface MonthCloseRunListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  period_year: number;
  period_month: number;
  status: MonthCloseStatus;
  pre_check_passed: boolean;
  posted: boolean;
  started_at: string | null;
  completed_at: string | null;
  external_source?: string | null;
}

export interface PreCheckRequest {
  organization_id: string;
  period_year: number;
  period_month: number;
}

export interface PreCheckResponse {
  passed: boolean;
  warnings: PreCheckWarning[];
  can_proceed: boolean;
}

export interface RunMonthCloseRequest {
  organization_id: string;
  period_year: number;
  period_month: number;
  skip_pre_check?: boolean;
  comment?: string | null;
}


const BASE = '/documents/month-close-runs';


export const monthCloseRuns = {
  list: (params?: {
    organization_id?: string;
    period_year?: number;
    period_month?: number;
    status?: MonthCloseStatus;
    limit?: number;
  }) =>
    api.get<Page<MonthCloseRunListItem>>(BASE, { params }).then((r) => r.data),

  get: (id: string) =>
    api.get<MonthCloseRun>(`${BASE}/${id}`).then((r) => r.data),

  remove: (id: string) =>
    api.delete(`${BASE}/${id}`).then(() => undefined),

  preCheck: (payload: PreCheckRequest) =>
    api.post<PreCheckResponse>(`${BASE}/pre-check`, payload).then((r) => r.data),

  run: (payload: RunMonthCloseRequest) =>
    api.post<MonthCloseRun>(`${BASE}/run`, payload).then((r) => r.data),
};
